// Utility functions for handling HTML content from API responses

/**
 * Sanitizes HTML content for safe rendering
 * Preserves formatting while removing dangerous elements
 * @param {string} html - HTML string from API response
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  let sanitized = html;
  
  // Remove dangerous tags and attributes for XSS protection
  sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, ''); // Remove script tags
  sanitized = sanitized.replace(/<iframe[^>]*>.*?<\/iframe>/gi, ''); // Remove iframes
  sanitized = sanitized.replace(/<object[^>]*>.*?<\/object>/gi, ''); // Remove objects
  sanitized = sanitized.replace(/<embed[^>]*>/gi, ''); // Remove embeds
  sanitized = sanitized.replace(/<form[^>]*>.*?<\/form>/gi, ''); // Remove forms
  sanitized = sanitized.replace(/<input[^>]*>/gi, ''); // Remove inputs
  sanitized = sanitized.replace(/<button[^>]*>.*?<\/button>/gi, ''); // Remove buttons
  sanitized = sanitized.replace(/<link[^>]*>/gi, ''); // Remove external links
  sanitized = sanitized.replace(/<meta[^>]*>/gi, ''); // Remove meta tags
  
  // Remove dangerous event handlers but preserve safe attributes
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, ''); // Remove event handlers like onclick
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript: protocols
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript: protocols
  
  // Allow data: URLs only for images (common use case)
  sanitized = sanitized.replace(/data:(?!image\/)/gi, ''); // Remove non-image data: protocols
  
  // Clean up any malformed or dangerous attributes
  sanitized = sanitized.replace(/style\s*=\s*["'][^"']*expression[^"']*["']/gi, ''); // Remove CSS expressions
  
  // Preserve common safe attributes while removing dangerous ones
  // This regex removes attributes that might contain scripts but keeps safe styling
  sanitized = sanitized.replace(/\s(src|href|action|formaction|background|dynsrc|lowsrc)\s*=\s*["'][^"']*javascript[^"']*["']/gi, '');
  
  return sanitized;
};

/**
 * Prepares HTML content for safe rendering in React
 * @param {string} html - HTML string from API response
 * @returns {object} - Object with __html property for dangerouslySetInnerHTML
 */
export const prepareHtmlForReact = (html) => {
  if (!html || typeof html !== 'string') return { __html: '' };
  
  const sanitized = sanitizeHtml(html);
  return { __html: sanitized };
};

/**
 * Detects if content contains HTML tags
 * Enhanced to ignore common false positives and catch more HTML patterns
 * @param {string} text - Text to check
 * @returns {boolean} - True if contains HTML tags
 */
export const containsHtml = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // More aggressive HTML tag detection
  const htmlTagPattern = /<[a-zA-Z][^>]*>/;
  const hasHtmlTags = htmlTagPattern.test(text);
  
  // Check for closing tags
  const hasClosingTags = /<\/[a-zA-Z]+>/.test(text);
  
  // Check for self-closing tags
  const hasSelfClosingTags = /<[a-zA-Z][^>]*\/>/.test(text);
  
  // Additional check for common HTML entities
  const hasHtmlEntities = /&[a-zA-Z0-9#]+;/.test(text);
  
  // Specific patterns from API responses (more robust detection)
  const hasCommonHtmlTags = /<(p|div|span|b|strong|i|em|u|br|h[1-6]|ul|ol|li|a)\b[^>]*>/i.test(text);
  
  // Enhanced detection for style attributes (common in API responses)
  const hasStyleAttributes = /style\s*=\s*["'][^"']*["']/i.test(text);
  
  // Check for your specific pattern like <b style="color: black;">
  const hasStyledTags = /<[a-zA-Z]+[^>]*style\s*=/.test(text);
  
  const result = hasHtmlTags || hasClosingTags || hasSelfClosingTags || hasHtmlEntities || 
                hasCommonHtmlTags || hasStyleAttributes || hasStyledTags;
  
  // Debug logging to help troubleshoot
  console.log('containsHtml debug:', {
    text: text.substring(0, 100) + (text.length > 100 ? '...' : ''),
    hasHtmlTags,
    hasClosingTags,
    hasSelfClosingTags,
    hasHtmlEntities,
    hasCommonHtmlTags,
    hasStyleAttributes,
    hasStyledTags,
    finalResult: result
  });
  
  return result;
};

/**
 * Converts HTML content to formatted plain text (fallback function)
 * Used only when HTML rendering is not desired
 * @param {string} html - HTML string from API response
 * @returns {string} - Formatted plain text
 */
export const htmlToFormattedText = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  let text = html;
  
  // Replace common HTML tags with appropriate text formatting
  text = text.replace(/<br\s*\/?>/gi, '\n'); // Line breaks
  text = text.replace(/<\/p>/gi, '\n\n'); // Paragraph breaks
  text = text.replace(/<p[^>]*>/gi, ''); // Remove opening p tags
  text = text.replace(/<\/div>/gi, '\n'); // Div breaks
  text = text.replace(/<div[^>]*>/gi, ''); // Remove opening div tags
  text = text.replace(/<b[^>]*>(.*?)<\/b>/gi, '$1'); // Bold text (keep content)
  text = text.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '$1'); // Strong text
  text = text.replace(/<i[^>]*>(.*?)<\/i>/gi, '$1'); // Italic text
  text = text.replace(/<em[^>]*>(.*?)<\/em>/gi, '$1'); // Emphasis text
  text = text.replace(/<u[^>]*>(.*?)<\/u>/gi, '$1'); // Underline text
  
  // Remove any remaining HTML tags
  text = text.replace(/<[^>]*>/g, '');
  
  // Decode HTML entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  text = tempDiv.textContent || tempDiv.innerText || '';
  
  // Clean up extra whitespace and line breaks
  text = text.replace(/\n\s*\n\s*\n/g, '\n\n'); // Max 2 consecutive line breaks
  text = text.replace(/^\s+|\s+$/g, ''); // Trim start and end
  
  return text;
};

/**
 * Enhanced check for mixed content (HTML + plain text)
 * @param {string} text - Text to analyze
 * @returns {object} - Analysis result with type and confidence
 */
export const analyzeContent = (text) => {
  if (!text || typeof text !== 'string') {
    return { type: 'empty', hasHtml: false, confidence: 1 };
  }
  
  const hasHtml = containsHtml(text);
  const htmlTagCount = (text.match(/<[^>]+>/g) || []).length;
  const totalLength = text.length;
  const htmlRatio = htmlTagCount > 0 ? (htmlTagCount * 10) / totalLength : 0; // Rough estimate
  
  if (!hasHtml) {
    return { type: 'plaintext', hasHtml: false, confidence: 1 };
  }
  
  if (htmlRatio > 0.1) {
    return { type: 'html', hasHtml: true, confidence: 0.9 };
  }
  
  return { type: 'mixed', hasHtml: true, confidence: 0.7 };
}; 